import { useUnit } from 'effector-react';
import { Popover } from 'radix-ui';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../shared/ui';
import { $tourStep, tourEnded, tourNext, TOUR_STEPS } from './model';

/**
 * The coach mark of the current tour step, anchored to `[data-tour="<step>"]`. Non-modal: the app
 * stays usable, Escape or "Skip tour" ends it.
 */
export function TourCoachMark() {
  const { t } = useTranslation();
  const [step, next, end] = useUnit([$tourStep, tourNext, tourEnded]);
  const id = step === null ? null : TOUR_STEPS[step];
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  // Find the anchor once the page has rendered it.
  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      setAnchor(id ? document.querySelector<HTMLElement>(`[data-tour="${id}"]`) : null);
    });
    return () => {
      cancelAnimationFrame(frame);
    };
  }, [id]);

  if (step === null || !id || !anchor) return null;
  const last = step === TOUR_STEPS.length - 1;
  return (
    <Popover.Root
      open
      onOpenChange={(open) => {
        if (!open) end();
      }}
    >
      <Popover.Anchor virtualRef={{ current: anchor }} />
      <Popover.Portal>
        <Popover.Content
          side={id === 'tree' ? 'right' : 'bottom'}
          align={id === 'charts' ? 'center' : 'start'}
          sideOffset={id === 'charts' ? -120 : 8}
          collisionPadding={16}
          onInteractOutside={(e) => {
            e.preventDefault();
          }}
          data-nonmodal
          aria-label={t('tour.label', { step: step + 1, total: TOUR_STEPS.length })}
          className="z-50 w-80 animate-scale-in space-y-2 rounded-lg border border-border-strong bg-bg-2 p-4 text-sm shadow-popover outline-none"
        >
          <p className="text-2xs font-medium text-text-3">
            {t('tour.label', { step: step + 1, total: TOUR_STEPS.length })}
          </p>
          <h2 className="text-base font-medium">{t(`tour.${id}.title`)}</h2>
          <p className="text-text-2">{t(`tour.${id}.body`)}</p>
          <div className="flex items-center justify-between pt-1">
            <Button size="sm" variant="ghost" onClick={end}>
              {t('tour.skip')}
            </Button>
            <Button size="sm" variant="primary" onClick={next}>
              {last ? t('tour.done') : t('tour.next')}
            </Button>
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
